import {
    RocketLaunchIcon,
    HomeIcon,
    PhoneIcon,
    PhoneArrowUpRightIcon,
    ChatBubbleLeftRightIcon,
    BookOpenIcon,
    LifebuoyIcon,
    CircleStackIcon,
    PuzzlePieceIcon,
    ChartBarIcon,
    CogIcon,
} from "@heroicons/react/24/outline";
import type { IconComponent } from "../../components/sidebar/navConfig";

/**
 * The Get Started documentation set.
 *
 * Two tiers per section from the same section entry: `quickstart` is the
 * short illustrated path to a working setup, `manual` is the exhaustive
 * reference. Both are markdown under `quickstart/` and `manual/`.
 *
 * Loaders are lazy `?raw` imports rather than eager ones so a docs page never
 * pulls the whole corpus into the route chunk, and `import.meta.glob` is
 * avoided so `tsconfig.json` does not need the `vite/client` ambient types.
 *
 * `navLabel` must stay identical to the sidebar label in
 * `components/sidebar/navConfig.ts` — the docs tell a reader to click that
 * exact word, and `docsManifest.test.ts` fails the build if the two drift.
 */

export type DocTier = "quickstart" | "manual";

export type DocLoader = () => Promise<{ default: string }>;

export type DocSection = {
    slug: string;
    /** Sidebar label in the dashboard, or `null` for sections with no nav entry. */
    navLabel: string | null;
    title: string;
    summary: string;
    icon: IconComponent;
    /** Dashboard routes this section documents. Verified against `App.tsx`. */
    routes: string[];
    /** Surfaced as a callout when a section is partly or wholly role-gated. */
    roleNote?: string;
    /** `outline` sections ship headings only; prose is still being written. */
    status: "complete" | "outline";
    quickstart: DocLoader;
    manual: DocLoader;
};

export const TIER_META: Record<DocTier, { label: string; segment: string; tagline: string }> = {
    quickstart: {
        label: "Quick Start",
        segment: "get-started",
        tagline: "The short, illustrated path to a working setup.",
    },
    manual: {
        label: "Complete Manual",
        segment: "manual",
        tagline: "Every screen, field, and permission, documented in full.",
    },
};

export const DOC_TIERS: DocTier[] = ["quickstart", "manual"];

export const DOC_SECTIONS: DocSection[] = [
    {
        slug: "start-here",
        navLabel: null,
        title: "Start here",
        summary: "Create your workspace, invite your team, and get your first AI agent answering.",
        icon: RocketLaunchIcon,
        routes: ["/dashboard", "/settings/organization", "/knowledge-base/overview"],
        status: "complete",
        quickstart: () => import("./quickstart/start-here.md?raw"),
        manual: () => import("./manual/start-here.md?raw"),
    },
    {
        slug: "dashboard",
        navLabel: "Dashboard",
        title: "Dashboard",
        summary: "Your post-login landing page: what the tiles mean and how the view changes by role.",
        icon: HomeIcon,
        routes: ["/dashboard"],
        status: "complete",
        quickstart: () => import("./quickstart/dashboard.md?raw"),
        manual: () => import("./manual/dashboard.md?raw"),
    },
    {
        // Listed straight after Dashboard, and `navLabel` is Dashboard's,
        // because these pages are entries in that sidebar section. The slug
        // stays "insights" so published guide URLs and screenshot ids keep
        // resolving, and it is why the Help link resolves by route: one sidebar
        // group now spans two guides.
        slug: "insights",
        navLabel: "Dashboard",
        title: "Insights",
        summary: "Analytics across chat, voice, the calling floor, SMS, agent ratings, surveys, and website visitors — all under Dashboard.",
        icon: ChartBarIcon,
        routes: [
            "/visitor-dashboard",
            "/analytics/overview",
            "/analytics/widgets",
            "/analytics/calls",
            "/analytics/call-centre",
            "/analytics/call-widgets",
            "/analytics/sms",
            "/analytics/agent-ratings",
            "/analytics/survey-responses",
            "/analytics/monthly-insights",
            "/agent-analytics",
        ],
        roleNote: "Every entry here is hidden from agents, who see only the Dashboard overview.",
        status: "complete",
        quickstart: () => import("./quickstart/insights.md?raw"),
        manual: () => import("./manual/insights.md?raw"),
    },
    {
        slug: "ai-calls",
        navLabel: "AI Calls",
        title: "AI Calls",
        summary: "Stand up an AI voice agent that answers your phone, then route, log, and dial out with it.",
        icon: PhoneIcon,
        routes: ["/call/setup", "/call/widget", "/call/pipelines", "/call/phone-numbers", "/call/call-logs", "/call/campaigns"],
        roleNote: "Hidden from agents and supervisors.",
        status: "complete",
        quickstart: () => import("./quickstart/ai-calls.md?raw"),
        manual: () => import("./manual/ai-calls.md?raw"),
    },
    {
        slug: "call-centre",
        navLabel: "Call Centre",
        title: "Call Centre",
        summary: "The human calling floor: dialer, lead queue, inbound handling, scripts, routing, and supervision.",
        icon: PhoneArrowUpRightIcon,
        routes: [
            "/call-centre/dialer",
            "/call-centre/queue",
            "/call-centre/leads",
            "/call-centre/incoming",
            "/call-centre/missed",
            "/call-centre/callbacks",
            "/call-centre/history",
            "/call-centre/scripts",
            "/call-centre/templates",
            "/call-centre/follow-ups",
            "/call-centre/floor",
            "/call-centre/numbers",
            "/call-centre/routing",
            "/call-centre/usage",
        ],
        roleNote:
            "Every role sees this section, but Callbacks, Follow-ups, Floor, Routing, Numbers, and Usage are gated.",
        status: "complete",
        quickstart: () => import("./quickstart/call-centre.md?raw"),
        manual: () => import("./manual/call-centre.md?raw"),
    },
    {
        slug: "chat",
        navLabel: "Chat",
        title: "Chat",
        summary: "Put an AI chat widget on your site and build the conversation workflows behind it.",
        icon: ChatBubbleLeftRightIcon,
        routes: ["/chat-widgets/widgets", "/chat-widgets/test-widgets", "/workflows", "/workflows/templates"],
        roleNote: "Hidden from agents and supervisors.",
        status: "complete",
        quickstart: () => import("./quickstart/chat.md?raw"),
        manual: () => import("./manual/chat.md?raw"),
    },
    {
        slug: "knowledge",
        navLabel: "Knowledge",
        title: "Knowledge",
        summary: "Feed your agents your own content so answers come from your business, not guesswork.",
        icon: BookOpenIcon,
        routes: [
            "/knowledge-base/overview",
            "/knowledge-base/documents",
            "/knowledge-base/sources",
            "/knowledge-base/crawler",
            "/knowledge-base/settings",
        ],
        roleNote: "Hidden from agents and supervisors.",
        status: "complete",
        quickstart: () => import("./quickstart/knowledge.md?raw"),
        manual: () => import("./manual/knowledge.md?raw"),
    },
    {
        slug: "support",
        navLabel: "Support",
        title: "Support",
        summary: "The human side: live chat inbox, tickets, contacts, call takeover, CRM analytics, and the Users section's team management.",
        icon: LifebuoyIcon,
        routes: [
            "/support-chats",
            "/managed-support",
            "/support/live-calls",
            "/crm/tickets",
            "/crm/contacts",
            "/crm/analytics/self",
            "/crm/analytics/agent",
            "/crm/analytics/supervisor",
            "/crm/settings",
            "/admins",
            "/agents",
            "/supervisors",
            "/users/permissions",
        ],
        roleNote:
            "Support is visible to every role, with the analytics and settings entries owner or admin only. The Users pages it documents sit in their own owner-and-admin section, and Permissions within it is owner-only.",
        status: "complete",
        quickstart: () => import("./quickstart/support.md?raw"),
        manual: () => import("./manual/support.md?raw"),
    },
    {
        // Slug kept as "audience" after the section was renamed to Data, so
        // published guide URLs and screenshot ids keep resolving.
        slug: "audience",
        navLabel: "Data",
        title: "Data",
        summary: "Capture structured data from conversations, take bookings, and read the visitor analytics behind both.",
        icon: CircleStackIcon,
        routes: ["/data-collections", "/calendars"],
        roleNote: "Hidden from agents and supervisors.",
        status: "complete",
        quickstart: () => import("./quickstart/audience.md?raw"),
        manual: () => import("./manual/audience.md?raw"),
    },
    {
        slug: "integrations",
        navLabel: "Integrations",
        title: "Integrations",
        summary: "Connect Shopify, your CRM, Google Sheets, Meta, LinkedIn, email, and SMS.",
        icon: PuzzlePieceIcon,
        routes: [
            "/integration/overview",
            "/integration/sheets",
            "/integration/salesforce",
            "/integration/zoho",
            "/integration/hubspot",
            "/integration/monday",
            "/integration/shopify",
            "/integration/meta",
            "/integration/meta-messaging",
            "/integration/linkedin",
            "/integration/email",
            "/integration/sms",
        ],
        roleNote: "Hidden from agents and supervisors.",
        status: "complete",
        quickstart: () => import("./quickstart/integrations.md?raw"),
        manual: () => import("./manual/integrations.md?raw"),
    },
    {
        slug: "setup",
        navLabel: "Setup",
        title: "Setup",
        summary: "Profile, organization policies, security, API keys, billing, usage, and referrals.",
        icon: CogIcon,
        routes: [
            "/settings/profile",
            "/settings/organization",
            "/settings/reports",
            "/settings/escalation",
            "/settings/notifications",
            "/settings/security",
            "/settings/api-keys",
            "/settings/usage-history",
            "/subscription",
            "/payment-methods",
            "/referrals/links",
            "/referrals/earnings",
        ],
        roleNote: "Always visible; Organization, Reports, and Escalation are owner-only, and API keys and billing are owner or admin.",
        status: "complete",
        quickstart: () => import("./quickstart/setup.md?raw"),
        manual: () => import("./manual/setup.md?raw"),
    },
];

export const getDocSection = (slug: string | undefined): DocSection | undefined =>
    slug ? DOC_SECTIONS.find((section) => section.slug === slug) : undefined;

export const docPath = (tier: DocTier, slug: string): string => `/docs/${TIER_META[tier].segment}/${slug}`;

/**
 * Sidebar group id (`navConfig.ts` `NavGroup.id`) → doc slug, so the in-app Help
 * link lands on the guide for the section the user is actually in. `call` and
 * `users` diverge — the latter shares the Support guide, which is where team
 * management is documented — and the rest are identical strings, listed anyway
 * so `docsManifest.test.ts` can assert every group is covered.
 *
 * A group maps to exactly one slug, which is why `docsHelpPath` prefers the
 * route: Dashboard spans both the `dashboard` guide and the `insights` one.
 */
export const NAV_GROUP_DOC_SLUG: Record<string, string> = {
    dashboard: "dashboard",
    call: "ai-calls",
    "call-centre": "call-centre",
    chat: "chat",
    knowledge: "knowledge",
    support: "support",
    users: "support",
    audience: "audience",
    integrations: "integrations",
    setup: "setup",
};

/**
 * The guide whose documented routes best cover `pathname`.
 *
 * Sections with no nav entry are skipped: `start-here` lists `/dashboard` and
 * two other landmarks it merely passes through, and it must not shadow the
 * guide that actually documents them.
 */
const docSlugForRoute = (pathname: string): string | undefined => {
    let best: { slug: string; length: number } | undefined;
    for (const section of DOC_SECTIONS) {
        if (section.navLabel === null) continue;
        for (const route of section.routes) {
            if (pathname !== route && !pathname.startsWith(route + "/")) continue;
            if (!best || route.length > best.length) best = { slug: section.slug, length: route.length };
        }
    }
    return best?.slug;
};

/** The guide covering where the reader is, falling back to their sidebar group. */
export const docsHelpSection = (navGroupId: string | undefined, pathname?: string): DocSection | undefined =>
    getDocSection(
        (pathname ? docSlugForRoute(pathname) : undefined) ??
            (navGroupId ? NAV_GROUP_DOC_SLUG[navGroupId] : undefined),
    );

/** Quick Start path for where the reader is, falling back to their group, then the hub. */
export const docsHelpPath = (navGroupId: string | undefined, pathname?: string): string => {
    const section = docsHelpSection(navGroupId, pathname);
    return section ? docPath("quickstart", section.slug) : "/docs";
};

export const loadDoc = async (section: DocSection, tier: DocTier): Promise<string> => {
    const loaded = await (tier === "quickstart" ? section.quickstart() : section.manual());
    return loaded.default;
};

/** Rough reading time, used as an at-a-glance signal on the hub and article header. */
export const readingMinutes = (markdown: string): number => {
    const words = markdown.trim().split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 220));
};
